// src/views/examples/Customer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Container,
  Row,
  Table,
  Input,
  Col,
} from "reactstrap";
import Header from "components/Headers/Header";
import { useTablePaging, PAGE_SIZE_OPTIONS } from "../stores/pagingStore";
import LoadingSpinner from "components/LoadingSpinner";
import Swal from "sweetalert2";

const TABLE_KEY = "customer";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");
const API_CUSTOMERS = `${API_BASE}/customers`;

const VIEWER_BASE = "https://fms.zonedevnode.com/viewer/?customerId=";

const Customer = () => {
  const navigate = useNavigate();
  const { page, pageSize, setPage, setPageSize } = useTablePaging(TABLE_KEY);

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [keyword, setKeyword] = useState("");

  // ✅ map: customerId -> { loading:boolean, hasActive:boolean }
  const [reportActiveMap, setReportActiveMap] = useState({});

  const ACTOR = localStorage.getItem("username") || "";

  const [isSmall, setIsSmall] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsSmall(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(API_CUSTOMERS, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json = await res.json();
      const arr = Array.isArray(json?.data) ? json.data : [];

      const mapped = arr.map((x) => ({
        customerId: x.customerId,
        customerName: x.customerName,
        status: x.status,
        notes: x.notes ?? "",

        connectionId: x.connection?.connectionId ?? null,
        connectionName: x.connection?.connectionName ?? "-",
        host: x.connection?.host ?? "-",
        port: String(x.connection?.port ?? ""),
        dbName: x.connection?.databaseName ?? "-",
        username: x.connection?.username ?? "",
        password: "",
        isActive: x.connection?.isActive ?? null,

        createdAt: x.createdAt,
        updatedAt: x.updatedAt,
      }));

      setRows(mapped);
      setPage(TABLE_KEY, 1);

      // reset map เพราะ list เปลี่ยน
      setReportActiveMap({});
    } catch (err) {
      console.error("fetchCustomers error:", err);
      setRows([]);
      setPage(TABLE_KEY, 1);
      setReportActiveMap({});
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const norm = (v) => (v ?? "").toString().toLowerCase().trim();

  // ✅ โชว์เฉพาะ status ACTIVE + search
  const filteredRows = useMemo(() => {
    const k = norm(keyword);

    return (
      rows
        // ✅ โชว์เฉพาะ ACTIVE และ INACTIVE
        .filter((r) =>
          ["ACTIVE", "INACTIVE"].includes((r?.status || "").toUpperCase()),
        )
        // ✅ search
        .filter((r) => {
          if (!k) return true;
          const blob = [
            r.customerId,
            r.customerName,
            r.notes,
            r.connectionName,
            r.host,
            r.port,
            r.dbName,
            r.status,
          ]
            .filter(Boolean)
            .join(" ");
          return norm(blob).includes(k);
        })
    );
  }, [rows, keyword]);

  const total = filteredRows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pageCount);
  const startIdx = (current - 1) * pageSize;
  const displayRows = filteredRows.slice(startIdx, startIdx + pageSize);

  const buildPages = (p, max) => {
    const arr = [];
    const start = Math.max(1, p - 1);
    const end = Math.min(max, p + 1);
    if (start > 1) arr.push(1);
    for (let i = start; i <= end; i++) arr.push(i);
    if (end < max) arr.push(max);
    return [...new Set(arr)];
  };
  const pages = buildPages(current, pageCount);

  const handleAddCustomer = () => navigate("/add-customer");

  const goEdit = (row) => {
    navigate("/edit-customer", { state: { row } });
  };

  const onChangePageSize = (e) =>
    setPageSize(TABLE_KEY, Number(e.target.value));

  useEffect(() => {
    setPage(TABLE_KEY, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const openViewer = (customerId) => {
    if (!customerId) return;
    const url = `${VIEWER_BASE}${encodeURIComponent(String(customerId))}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // ✅ เช็คว่ามี report isActive=true ไหม (เฉพาะแถวที่กำลังแสดง)
  const checkHasActiveReport = async (customerId) => {
    if (!API_BASE || !customerId) {
      setReportActiveMap((prev) => ({
        ...prev,
        [customerId]: { loading: false, hasActive: false },
      }));
      return;
    }

    setReportActiveMap((prev) => ({
      ...prev,
      [customerId]: { loading: true, hasActive: false },
    }));

    try {
      const url = `${API_BASE}/customers/${encodeURIComponent(
        String(customerId),
      )}/reports`;

      const res = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json = await res.json();
      const list = Array.isArray(json?.reports) ? json.reports : [];

      const hasActive = list.some((x) => x?.isActive === true);

      setReportActiveMap((prev) => ({
        ...prev,
        [customerId]: { loading: false, hasActive },
      }));
    } catch (e) {
      console.error("checkHasActiveReport error:", e);
      setReportActiveMap((prev) => ({
        ...prev,
        [customerId]: { loading: false, hasActive: false },
      }));
    }
  };

  // ✅ ยิงเช็คเฉพาะ customer ที่อยู่ใน displayRows และยังไม่เคยเช็ค
  useEffect(() => {
    const ids = (displayRows || [])
      .map((r) => r.customerId)
      .filter(Boolean)
      .map(String);

    ids.forEach((id) => {
      const st = reportActiveMap[id];
      if (!st) checkHasActiveReport(id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayRows]);

  if (isLoading) return <LoadingSpinner fullPage />;

  const handleSuspend = async (row) => {
    const customerId = row?.customerId;
    if (!customerId) return;

    const result = await Swal.fire({
      title: "Confirm Suspend?",
      text: `Do you want to suspend customer ${row?.customerName || customerId} ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Suspend",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      setIsLoading(true);

      const url = `${API_BASE}/customers/${encodeURIComponent(
        String(customerId),
      )}`;

      const res = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "x-actor": ACTOR,
        },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${text}`);
      }

      await Swal.fire({
        icon: "success",
        title: "Suspend successful",
        text: "Successfully suspended customer",
        timer: 1500,
        showConfirmButton: false,
      });

      await fetchCustomers();
    } catch (e) {
      console.error("suspend customer error:", e);

      Swal.fire({
        icon: "error",
        title: "Suspend failed",
        text: "Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow bg-secondary">
              <CardHeader className="border-0 d-flex align-items-center justify-content-between">
                <h6 className="mb-0">Customer</h6>
                <Button color="success" onClick={handleAddCustomer}>
                  Add Customer
                </Button>
              </CardHeader>

              <CardBody>
                {/* Toolbar */}
                <div className="mb-3">
                  <Row className="align-items-center g-0">
                    <Col
                      xs="12"
                      md="6"
                      className="d-flex align-items-center flex-wrap"
                      style={{ gap: 8 }}
                    >
                      <Input
                        type="select"
                        value={pageSize}
                        onChange={onChangePageSize}
                        style={{ width: 96 }}
                      >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Input>
                      <span className="text-muted">row/page</span>
                    </Col>

                    <Col
                      xs="12"
                      md="6"
                      className="mt-2 mt-md-0 d-flex justify-content-md-end"
                    >
                      <Input
                        type="text"
                        placeholder="customer/connection/host/db"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        className="table-modern-search w-100 w-md-auto"
                        style={{ maxWidth: 420, minWidth: 280 }}
                      />
                    </Col>
                  </Row>
                </div>

                {/* TABLE */}
                {isSmall ? (
                  <div className="table-responsive table-modern-wrap">
                    <Table responsive hover className="table table-modern mb-0">
                      <thead className="thead-colored">
                        <tr>
                          <th className="text-start">#</th>
                          <th className="text-start" style={{ width: 320 }}>
                            Customer Name
                          </th>
                          <th className="text-start" style={{ width: 220 }}>
                            Connection
                          </th>
                          <th className="text-start" style={{ width: 220 }}>
                            Host
                          </th>
                          <th className="text-start" style={{ width: 220 }}>
                            DB Name
                          </th>
                          <th className="text-start" style={{ width: 120 }}>
                            Report viewer
                          </th>
                          <th className="text-start" style={{ width: 220 }}>
                            Management
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayRows.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center text-muted">
                              ไม่พบข้อมูลลูกค้า
                            </td>
                          </tr>
                        ) : (
                          displayRows.map((r, i) => {
                            const rowNo = startIdx + i + 1;
                            const st = reportActiveMap[
                              String(r.customerId)
                            ] || {
                              loading: true,
                              hasActive: false,
                            };
                            const canOpenViewer = !st.loading && st.hasActive;

                            return (
                              <tr
                                key={r.customerId || `${r.customerName}-${i}`}
                              >
                                <td data-th="#">{rowNo}</td>
                                <td data-th="Customer Name">
                                  {r.customerName}
                                </td>
                                <td data-th="Connection">{r.connectionName}</td>
                                <td data-th="Host">
                                  {r.host}:{r.port}
                                </td>
                                <td data-th="DB Name">{r.dbName}</td>
                                <td data-th="Report viewer">
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => openViewer(r.customerId)}
                                    disabled={!canOpenViewer}
                                    title={
                                      st.loading
                                        ? "Checking reports..."
                                        : st.hasActive
                                          ? "Open viewer"
                                          : "No active report"
                                    }
                                  >
                                    View
                                  </Button>
                                </td>
                                <td data-th="Management">
                                  <div className="d-inline-flex gap-2 flex-wrap">
                                    <Button
                                      color="success"
                                      size="sm"
                                      onClick={() =>
                                        navigate("/report-customer", {
                                          state: { customer: r },
                                        })
                                      }
                                    >
                                      Report
                                    </Button>
                                    <Button
                                      color="warning"
                                      size="sm"
                                      onClick={() => goEdit(r)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      color="danger"
                                      size="sm"
                                      onClick={() => handleSuspend(r)}
                                    >
                                      Suspend
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="zi-table-wrap">
                    <Table responsive hover className="zi-table mb-0">
                      <thead>
                        <tr>
                          <th className="text-start" style={{ width: 90 }}>
                            #
                          </th>
                          <th className="text-start" style={{ width: 300 }}>
                            Customer Name
                          </th>
                          <th className="text-start" style={{ width: 220 }}>
                            Connection Name
                          </th>
                          <th className="text-start" style={{ width: 220 }}>
                            Host
                          </th>
                          <th className="text-start" style={{ width: 220 }}>
                            DB Name
                          </th>
                          <th className="text-center" style={{ width: 120 }}>
                            Report viewer
                          </th>
                          <th className="text-center" style={{ width: 320 }}>
                            Management
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {displayRows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={6}
                              className="text-center text-muted py-4"
                            >
                              ไม่พบข้อมูลลูกค้า
                            </td>
                          </tr>
                        ) : (
                          displayRows.map((r, i) => {
                            const rowNo = startIdx + i + 1;
                            const st = reportActiveMap[
                              String(r.customerId)
                            ] || {
                              loading: true,
                              hasActive: false,
                            };
                            const canOpenViewer = !st.loading && st.hasActive;

                            return (
                              <tr
                                key={r.customerId || `${r.customerName}-${i}`}
                              >
                                <td className="text-start fw-600">{rowNo}</td>
                                <td className="text-start fw-600">
                                  {r.customerName}
                                </td>
                                <td className="text-start">
                                  {r.connectionName}
                                </td>
                                <td className="text-start">
                                  {r.host}:{r.port}
                                </td>
                                <td className="text-start">{r.dbName}</td>
                                <td className="text-center">
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => openViewer(r.customerId)}
                                    disabled={!canOpenViewer}
                                    title={
                                      st.loading
                                        ? "Checking reports..."
                                        : st.hasActive
                                          ? "Open viewer"
                                          : "No active report"
                                    }
                                  >
                                    View
                                  </Button>
                                </td>
                                <td className="text-end">
                                  <div className="d-inline-flex gap-2 justify-content-end flex-wrap">
                                    <Button
                                      color="success"
                                      size="sm"
                                      onClick={() =>
                                        navigate("/report-customer", {
                                          state: { customer: r },
                                        })
                                      }
                                    >
                                      Report
                                    </Button>
                                    <Button
                                      color="warning"
                                      size="sm"
                                      onClick={() => goEdit(r)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      color="danger"
                                      size="sm"
                                      onClick={() => handleSuspend(r)}
                                    >
                                      Suspend
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* summary */}
                <div className="d-flex justify-content-end mt-2">
                  <div className="text-muted small">
                    Showing {total === 0 ? 0 : startIdx + 1}–
                    {Math.min(startIdx + displayRows.length, total)} of {total}{" "}
                    entries
                  </div>
                </div>

                <style>{`
                  .zi-table-wrap{
                    background:#fff;
                    border-radius:10px;
                    border:1px solid rgba(0,0,0,.06);
                    overflow:hidden;
                  }
                  .zi-table{ margin:0; }
                  .zi-table thead th{
                    background:#f7fafc;
                    color:#0f172a;
                    font-weight:700;
                    font-size:14px;
                    border-bottom:1px solid rgba(0,0,0,.06);
                    padding:14px 16px;
                    white-space:nowrap;
                  }
                  .zi-table tbody td{
                    padding:14px 16px;
                    border-top:1px solid rgba(0,0,0,.06);
                    vertical-align:middle;
                    font-size:14px;
                    color:#0f172a;
                  }
                  .zi-table tbody tr:hover{ background:#f9fbff; }
                  .fw-600{ font-weight:600; }
                  .gap-2{ gap:8px; }
                `}</style>
              </CardBody>

              {/* Pagination */}
              {total > 0 && (
                <CardFooter
                  className="py-4"
                  style={{ position: "relative", zIndex: 10 }}
                >
                  <div className="paging-wrap">
                    <button
                      className="paging-btn icon"
                      onClick={() =>
                        setPage(TABLE_KEY, Math.max(1, current - 1))
                      }
                      disabled={current === 1}
                    >
                      ‹
                    </button>

                    {pages.map((p) => (
                      <button
                        key={p}
                        className={`paging-btn ${current === p ? "active" : ""}`}
                        onClick={() => setPage(TABLE_KEY, p)}
                      >
                        {p}
                      </button>
                    ))}

                    <button
                      className="paging-btn icon"
                      onClick={() =>
                        setPage(TABLE_KEY, Math.min(pageCount, current + 1))
                      }
                      disabled={current === pageCount}
                      aria-label="Next page"
                    >
                      ›
                    </button>
                  </div>
                </CardFooter>
              )}
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default Customer;
